/* */
import React, { Fragment, useCallback } from 'react';
import { suppressNonNumericInput } from 'helpers';
import Services from 'services';
import { optionGenders, SIZE_ICON } from 'consts/data';
import { maxLengthName } from 'consts/validateSchema';
import { FastField, Field, useFormikContext } from 'formik';
import InputField from 'customField/InputField';
import SelectField from 'customField/SelectField';
import NewDesign from 'components/NewDesign';
import classNames from 'classnames';
import { showError } from 'helpers/handleError';
import { CommonIcons } from 'components/Icons';
import ThreeDotAnimated from 'components/ThreeDotAnimated';
import Loading from 'components/Loading';
import { useCheckingBillingInformationProvider } from 'providers/CheckingBillingInformationProvider';
import { TooltipReactTooltip } from 'components/Tooltip';
import UserModel from 'models/user.model';
import useToggleDialog from 'hooks/useToggleDialog';
import DialogRejectBillingInfo from './Dialog/DialogRejectBillingInfo';
import useGetUserServlet from 'hooks/user/useGetUserServlet';
import BillingInfoComparison from 'components/BillingInfoComparison';

let Timer;
const Profile = ({ hidePersonalInformation }) => {
  const { values, setFieldValue, isSubmitting, setSubmitting, handleChange, errors } =
    useFormikContext();
  const checkingBillingProcess = useCheckingBillingInformationProvider();
  const [refetchUserServlet] = useGetUserServlet({ shouldTrigger: false });
  const [openRejectReason, toggleRejectReason, shouldRenderRejectReason] = useToggleDialog(false);

  // Some special case
  const onChangePhone = useCallback(
    (setFieldValue) => {
      return (e) => {
        try {
          const { name, value } = e.target;
          setFieldValue(name, value);
          clearTimeout(Timer);
          Timer = setTimeout(async () => {
            const res = await Services.formatNumberFree({
              countryCode: '',
              number: value,
            });
            setFieldValue(name, res.data);
          }, 500);
        } catch (error) {
          showError(error);
        }
      };
    },
    [values.myAccountPhone]
  );

  const personalInformation = useCallback(
    ({ values, setFieldValue }) => {
      const { myAccountCountry } = values;
      return [
        {
          label: 'First Name: ',
          required: true,
          input: (
            <FastField
              component={InputField}
              name='myAccountFirstname'
              maxLength={maxLengthName}
              placeholder='First name'
            />
          ),
        },
        {
          label: 'Last Name: ',
          required: true,
          input: (
            <FastField
              component={InputField}
              name='myAccountLastname'
              maxLength={maxLengthName}
              placeholder='Last name'
            />
          ),
        },
        {
          label: 'Phone: ',
          input: (
            <FastField
              component={InputField}
              name='myAccountPhone'
              maxLength={maxLengthName}
              onKeyDown={suppressNonNumericInput}
              onChangeCustomize={onChangePhone(setFieldValue)}
              placeholder='Phone'
            />
          ),
        },
        {
          label: 'Gender: ',
          input: (
            <FastField
              component={SelectField}
              name='myAccountGender'
              options={optionGenders}
              isSearchable={false}
              placeholder='Select gender'
            />
          ),
        },
        {
          label: 'Address1: ',
          input: (
            <FastField component={InputField} name='myAccountAddress1' placeholder='Address 1' />
          ),
        },
        {
          label: 'Address2: ',
          input: (
            <FastField component={InputField} name='myAccountAddress2' placeholder='Address 2' />
          ),
        },
        {
          label: 'City: ',
          input: <FastField component={InputField} name='myAccountCity' placeholder='City' />,
        },
        {
          label: 'State: ',
          input: <FastField component={InputField} name='myAccountState' placeholder='State' />,
        },
        {
          label: 'Zip: ',
          input: <FastField component={InputField} name='myAccountZip' placeholder='Zip' />,
        },
        {
          label: 'Country: ',
          input: myAccountCountry || 'none',
        },
      ];
    },
    [values]
  );

  const billingInformation = () => {
    const commonProps = () => {
      if (checkingBillingProcess.isInProgress) {
        return {
          readOnly: true,
        };
      }

      return undefined;
    };

    return [
      {
        label: UserModel.mappingLabelBillingInfo('billingName'),
        required: true,
        input: (
          <Field
            {...commonProps()}
            component={InputField}
            name='myBillingName'
            placeholder='John Abraham'
          />
        ),
      },
      {
        label: UserModel.mappingLabelBillingInfo('myEntityName'),
        required: true,
        input: (
          <Field
            {...commonProps()}
            component={InputField}
            name='myEntityName'
            placeholder='Entity name'
          />
        ),
      },
      {
        label: UserModel.mappingLabelBillingInfo('billingAddress'),
        required: true,
        input: (
          <Field
            {...commonProps()}
            component={InputField}
            name='myBillingAddress'
            placeholder='Address'
          />
        ),
      },
      {
        label: UserModel.mappingLabelBillingInfo('billingCountry'),
        required: true,
        input: (
          <Field
            {...commonProps()}
            component={InputField}
            name='myBillingCountry'
            placeholder='US'
          />
        ),
      },
      {
        label: UserModel.mappingLabelBillingInfo('billingRegNo'),
        required: true,
        input: (
          <Field
            {...commonProps()}
            component={InputField}
            name='myBillingRegNo'
            placeholder='12AQAA211'
          />
        ),
      },
      {
        label: UserModel.mappingLabelBillingInfo('billingVatNo'),
        required: true,
        input: (
          <Field
            {...commonProps()}
            component={InputField}
            name='myBillingVatNo'
            placeholder='QE12112XZ34'
          />
        ),
      },

      {
        label: UserModel.mappingLabelBillingInfo('billingIBAN'),
        required: true,
        input: (
          <Field
            {...commonProps()}
            component={InputField}
            name='myBillingIBAN'
            placeholder='IBAN'
          />
        ),
      },
      {
        label: UserModel.mappingLabelBillingInfo('billingBIC'),
        required: true,
        input: (
          <Field
            {...commonProps()}
            component={InputField}
            name='myBillingBIC'
            placeholder='BIC / SWIFT CODE'
          />
        ),
      },
      {
        label: UserModel.mappingLabelBillingInfo('billingIntermediaryBIC'),
        required: true,
        input: (
          <Field
            {...commonProps()}
            component={InputField}
            name='myBillingIntermediaryBIC'
            placeholder='Intermediary BIC'
          />
        ),
      },
      {
        label: UserModel.mappingLabelBillingInfo('billingBank'),
        required: true,
        input: (
          <Field
            {...commonProps()}
            component={InputField}
            name='myBillingBank'
            placeholder='Your bank account'
          />
        ),
      },
      {
        label: UserModel.mappingLabelBillingInfo('billingBankAddress'),
        required: true,
        input: (
          <Field
            {...commonProps()}
            component={InputField}
            name='myBillingBankAddress'
            placeholder='Address'
          />
        ),
      },
    ];
  };

  const renderBillingInfoRequested = () => {
    return (
      <div
        className='renderBillingInfoRequested w-100 default-border p-3'
        style={{ borderStyle: 'dashed', borderWidth: 2, backgroundColor: 'rgba(0, 0, 0, 0.03)' }}
      >
        <NewDesign.Typography component='h5' className='mb-3'>
          Change requesting
        </NewDesign.Typography>

        <BillingInfoComparison
          className={'d-flex gap-12px flex-column'}
          originalInfo={checkingBillingProcess.originalInfo}
          nextInfo={checkingBillingProcess.draftInfo}
        />
      </div>
    );
  };

  const WrapperProps = {
    style: { display: 'grid', gridTemplateColumns: '1fr 300px', gap: 24 },
  };

  return (
    <div className='component:Profile pb-2 d-flex gap-40px mobile:flex-column mb-2'>
      {!hidePersonalInformation && (
        <div className='flex-grow-1'>
          <NewDesign.Typography component='h5' className='mb-4'>
            Personal Information
          </NewDesign.Typography>

          <div {...WrapperProps}>
            {personalInformation({
              handleChange,
              isSubmitting,
              setSubmitting,
              values,
              errors,
              setFieldValue,
            }).map((el) => (
              <Fragment key={el.label}>
                <NewDesign.Typography className={classNames(el.required && 'required')}>
                  {el.label}
                </NewDesign.Typography>
                <NewDesign.Typography>{el.input}</NewDesign.Typography>
              </Fragment>
            ))}
          </div>
        </div>
      )}

      <div className='flex-grow-1'>
        <div className='mb-4 d-flex justify-content-between flex-wrap'>
          <NewDesign.Typography component='h5'>Billing Information</NewDesign.Typography>

          {shouldRenderRejectReason && (
            <DialogRejectBillingInfo
              isOpen={openRejectReason}
              toggle={toggleRejectReason}
              rejectReason={checkingBillingProcess.rejectReason}
              originalInfo={checkingBillingProcess.originalInfo}
              draftInfo={checkingBillingProcess.draftInfo}
            />
          )}

          {/* Status billing information */}
          <div className='status-billing-information' style={{ minHeight: 30 }}>
            {checkingBillingProcess.isRejected && (
              <div className='d-flex gap-8px align-items-center'>
                <NewDesign.Badge color='danger'>
                  Billing information verification is failed!
                </NewDesign.Badge>
                <NewDesign.Typography
                  className='color-danger-v2 is-hover'
                  onClick={toggleRejectReason}
                >
                  <CommonIcons.EyeOpen size={SIZE_ICON} /> See reason
                </NewDesign.Typography>
              </div>
            )}

            {checkingBillingProcess.isNotHaveBilling && (
              <NewDesign.Badge color='danger'>
                <CommonIcons.Warning className='color-danger-v2 mr-1' size={SIZE_ICON} /> Billing
                information need to be updated!
              </NewDesign.Badge>
            )}

            {checkingBillingProcess.isVerified && (
              <NewDesign.Badge color='success'>
                <CommonIcons.Checked size={SIZE_ICON} className='color-success-v2 mr-1' /> Verified
              </NewDesign.Badge>
            )}

            {checkingBillingProcess.isInProgress ? (
              checkingBillingProcess.refreshingBillingInformation ? (
                <Loading />
              ) : (
                <div className='d-flex gap-6px align-items-center'>
                  <NewDesign.Badge className='d-flex gap-8px'>
                    <Loading size='sm' />
                    <NewDesign.Typography>
                      Billing information is being verified <ThreeDotAnimated />
                    </NewDesign.Typography>
                  </NewDesign.Badge>

                  <NewDesign.Tooltip
                    id='tooltip-update-billing-info'
                    message={
                      <NewDesign.Typography>
                        We need to verify your information to determine its validity.
                        <br /> If this process takes too long, please contact to our supports!
                      </NewDesign.Typography>
                    }
                  />

                  <TooltipReactTooltip
                    idTarget='refresh-checking-billing'
                    messageToolTip='Refresh status billing information'
                    place='top'
                  >
                    <CommonIcons.Refresh
                      size={SIZE_ICON}
                      className='is-hover'
                      onClick={() =>
                        checkingBillingProcess.refreshBillingInformation(
                          ({ isVerified, isRejected }) => {
                            if (isVerified || isRejected) {
                              refetchUserServlet();
                            }
                          }
                        )
                      }
                    />
                  </TooltipReactTooltip>
                </div>
              )
            ) : null}
          </div>
        </div>

        {checkingBillingProcess.isInProgress ? (
          renderBillingInfoRequested()
        ) : (
          <div {...WrapperProps}>
            {billingInformation().map((el) => (
              <Fragment key={el.label}>
                <NewDesign.Typography className={classNames(el.required && 'required')}>
                  {el.label}:
                </NewDesign.Typography>
                <NewDesign.Typography>{el.input}</NewDesign.Typography>
              </Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(Profile);
